// docs/185 PB13: how a map of regions is drawn apart from its art. A region the story has not reached yet lies under
// a veil (its colours drained, dimmed and cooled toward the fog, feathered over a couple of cells at its border); the
// Underdark sits sunken below the land round it (a little darker all through, and darker still in the shadow its
// north rim throws). Pure fields per cell, then one pass over the chart's pixels (`shadeRegions`), so the veil is part
// of the painted chart and costs nothing while the camera moves. No new colours: the fog is the void's own navy.
import type { CSSProperties } from 'react';
import type { Overworld } from './overworld';

const CELL = 24;

export interface RegionFields {
  w: number; h: number;
  /** 0..1 per cell: how far under a locked region's veil. */
  veil: Float32Array;
  /** 0..1 per cell: how far sunk into the Underdark's shadow. */
  sink: Float32Array;
}

/** How much the veil drains, dims and fogs a locked region at its heart. */
export const VEIL = { drain: 0.85, dim: 0.42, fog: 0.2 } as const;
/** The fog the veil leans toward (the chart's void, `.fk-ow-void`). */
const FOG: readonly [number, number, number] = [7, 11, 22];
/** The Underdark's sinking: all through it, and at most under its north rim, fading over `SHADOW_CELLS`. */
export const SINK = { floor: 0.12, rim: 0.3 } as const;
const SHADOW_CELLS = 4;

/** A 3×3 box blur of a field, `n` times (feathers the borders over about `n` cells). */
function blur(f: Float32Array, w: number, h: number, n: number): Float32Array {
  let a = f;
  for (let k = 0; k < n; k++) {
    const b = new Float32Array(w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let s = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const X = Math.max(0, Math.min(w - 1, x + dx)), Y = Math.max(0, Math.min(h - 1, y + dy));
        s += a[Y * w + X]!;
      }
      b[y * w + x] = s / 9;
    }
    a = b;
  }
  return a;
}

/** The veil and the sinking of a map of regions, or undefined for a one-country chart (or one with nothing to shade). */
export function regionFields(ow: Pick<Overworld, 'w' | 'h' | 'regions' | 'regionOf'>): RegionFields | undefined {
  const { w, h, regions, regionOf } = ow;
  if (!regions || !regionOf) return undefined;
  const at = (x: number, y: number) => regionOf[y]!.charCodeAt(x) - 97;
  const locked = new Float32Array(w * h), deep = new Float32Array(w * h), under = new Float32Array(w * h);
  let any = false;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const r = regions[at(x, y)]!;
    if (!r.unlocked) { locked[y * w + x] = 1; any = true; }
    if (r.biome === 'underdark') { deep[y * w + x] = 1; any = true; }
  }
  if (!any) return undefined;
  // the shadow under the rim: cells of the Underdark a few cells south of where the land above it ends
  for (let x = 0; x < w; x++) {
    let since = 99;
    for (let y = 0; y < h; y++) {
      if (!deep[y * w + x]) { since = 0; continue; }
      since++;
      if (since <= SHADOW_CELLS) under[y * w + x] = 1 - (since - 1) / SHADOW_CELLS;
    }
  }
  const veil = blur(locked, w, h, 2), d = blur(deep, w, h, 1), u = blur(under, w, h, 1);
  const sink = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) sink[i] = d[i]! * SINK.floor + u[i]! * SINK.rim * d[i]!;
  return { w, h, veil, sink };
}

/**
 * Shade a painted chart's pixels (RGBA, `w` × `h` cells of 24 px) by the fields: the veil drains, dims and fogs, the
 * sinking darkens; for the lights layer (`lights`) the veil fades the lit pixels instead. Each pixel reads the fields
 * between the four nearest cell centres, so the edges are soft.
 */
export function shadeRegions(px: Uint8ClampedArray, w: number, h: number, f: RegionFields, lights: boolean): void {
  const W = w * CELL;
  const field = (a: Float32Array, cx: number, cy: number) => {
    const x0 = Math.max(0, Math.min(w - 1, Math.floor(cx))), y0 = Math.max(0, Math.min(h - 1, Math.floor(cy)));
    const x1 = Math.min(w - 1, x0 + 1), y1 = Math.min(h - 1, y0 + 1);
    const u = Math.max(0, Math.min(1, cx - x0)), v = Math.max(0, Math.min(1, cy - y0));
    return (a[y0 * w + x0]! * (1 - u) + a[y0 * w + x1]! * u) * (1 - v) + (a[y1 * w + x0]! * (1 - u) + a[y1 * w + x1]! * u) * v;
  };
  for (let gy = 0; gy < h; gy++) for (let gx = 0; gx < w; gx++) {
    // a cell whose 3×3 neighbourhood is untouched stays as painted
    let touched = false;
    for (let dy = -1; dy <= 1 && !touched; dy++) for (let dx = -1; dx <= 1 && !touched; dx++) {
      const X = gx + dx, Y = gy + dy;
      if (X >= 0 && Y >= 0 && X < w && Y < h && (f.veil[Y * w + X]! > 0.004 || f.sink[Y * w + X]! > 0.004)) touched = true;
    }
    if (!touched) continue;
    for (let py = gy * CELL; py < (gy + 1) * CELL; py++) {
      const cy = (py + 0.5) / CELL - 0.5;
      for (let pxi = gx * CELL; pxi < (gx + 1) * CELL; pxi++) {
        const i = (py * W + pxi) * 4;
        if (px[i + 3] === 0) continue;
        const cx = (pxi + 0.5) / CELL - 0.5;
        const v = field(f.veil, cx, cy), s = field(f.sink, cx, cy);
        if (lights) { px[i + 3] = px[i + 3]! * (1 - 0.9 * v); continue; }
        let r = px[i]!, g = px[i + 1]!, b = px[i + 2]!;
        if (v > 0.004) {
          const lum = 0.3 * r + 0.59 * g + 0.11 * b, k = VEIL.drain * v, m = 1 - VEIL.dim * v, fg = VEIL.fog * v;
          r = (r + (lum - r) * k) * m; g = (g + (lum - g) * k) * m; b = (b + (lum - b) * k) * m;
          r += (FOG[0] - r) * fg; g += (FOG[1] - g) * fg; b += (FOG[2] - b) * fg;
        }
        if (s > 0.004) { r *= 1 - s; g *= 1 - s; b *= 1 - s; }
        px[i] = r; px[i + 1] = g; px[i + 2] = b;
      }
    }
  }
}

/** A cell of the map (whole cells) and the region it lies in, or undefined on a one-country chart. */
export function regionOfCell(ow: Pick<Overworld, 'w' | 'h' | 'regions' | 'regionOf'>, x: number, y: number): number | undefined {
  if (!ow.regions || !ow.regionOf) return undefined;
  const X = Math.max(0, Math.min(ow.w - 1, Math.floor(x))), Y = Math.max(0, Math.min(ow.h - 1, Math.floor(y)));
  return ow.regionOf[Y]!.charCodeAt(X) - 97;
}

/** Whether a point of the map lies in a region the story has not reached (its places are hidden, PB13). */
export function lockedAt(ow: Pick<Overworld, 'w' | 'h' | 'regions' | 'regionOf'>, x: number, y: number): boolean {
  const r = regionOfCell(ow, x, y);
  return r !== undefined && !ow.regions![r]!.unlocked;
}

/**
 * Where each road crosses into a locked region: the first cell of it past the border, and the way it was going
 * (the chart bars the road there). A road with both ends locked has none (it lies wholly under the veil).
 */
export function barredRoads(ow: Pick<Overworld, 'w' | 'h' | 'regions' | 'regionOf' | 'roads'>): Array<{ a: string; b: string; x: number; y: number; dx: number; dy: number }> {
  const out: Array<{ a: string; b: string; x: number; y: number; dx: number; dy: number }> = [];
  if (!ow.regions) return out;
  for (const r of ow.roads) {
    if (r.a.startsWith('edge:') || r.b.startsWith('edge:')) continue;
    const cells: Array<[number, number]> = [];
    for (let i = 0; i < r.path.length - 1; i++) {
      const [ax, ay] = r.path[i]!, [bx, by] = r.path[i + 1]!;
      const n = Math.max(Math.abs(bx - ax), Math.abs(by - ay));
      for (let s = i ? 1 : 0; s <= n; s++) cells.push([ax + ((bx - ax) * s) / Math.max(1, n), ay + ((by - ay) * s) / Math.max(1, n)]);
    }
    const locked = cells.map(([x, y]) => lockedAt(ow, x, y));
    if (locked[0] && locked[locked.length - 1]) continue;
    // walk from the open end toward the locked one
    const seq = locked[0] ? [...cells].reverse() : cells, lk = locked[0] ? [...locked].reverse() : locked;
    const i = lk.indexOf(true);
    if (i <= 0) continue;
    const [x, y] = seq[i - 1]!, [nx, ny] = seq[i]!;
    out.push({ a: r.a, b: r.b, x, y, dx: Math.sign(nx - x), dy: Math.sign(ny - y) });
  }
  return out;
}

/** A mask image of the cells `keep` keeps (one pixel a cell, scaled smooth over the stage), as a data URL; browser only. */
export function regionMaskUrl(ow: Pick<Overworld, 'w' | 'h' | 'regions' | 'regionOf'>, keep: (region: number) => boolean): string | undefined {
  if (!ow.regions || !ow.regionOf || typeof document === 'undefined') return undefined;
  try {
    const c = document.createElement('canvas');
    c.width = ow.w; c.height = ow.h;
    const ctx = c.getContext('2d');
    if (!ctx) return undefined;
    const img = ctx.createImageData(ow.w, ow.h);
    for (let y = 0; y < ow.h; y++) for (let x = 0; x < ow.w; x++) if (keep(ow.regionOf[y]!.charCodeAt(x) - 97)) img.data.set([0, 0, 0, 255], (y * ow.w + x) * 4);
    ctx.putImageData(img, 0, 0);
    return c.toDataURL();
  } catch { return undefined; }
}

/** A mask over the stage (the land light and land sky): only the cells of the image show. */
export function stageMask(url: string | undefined): CSSProperties | undefined {
  if (!url) return undefined;
  return { maskImage: `url(${url})`, maskSize: '100% 100%', maskRepeat: 'no-repeat' };
}

/**
 * A mask over the whole view (the weather and the season's particles, which do not pan with the stage) that cuts the
 * cells of the image out, lined up with the stage through the camera's `--ow-cam-*` variables.
 */
export function viewCutMask(url: string | undefined, w: number, h: number): CSSProperties | undefined {
  if (!url) return undefined;
  const size = `calc(${w} * var(--ow-cell) * var(--ow-cam-z, 1)) calc(${h} * var(--ow-cell) * var(--ow-cam-z, 1))`;
  return {
    maskImage: `linear-gradient(#000, #000), url(${url})`, maskSize: `100% 100%, ${size}`, maskPosition: '0 0, var(--ow-cam-x, 0px) var(--ow-cam-y, 0px)',
    maskRepeat: 'no-repeat', maskComposite: 'subtract',
  };
}
